/* global setTimeout */

import * as React from "react";
import { useState } from "react";
import { CoObjectGeneralInfo, CoObjectItem } from "../../interfaces/CoObject";
import { Toggle } from "@fluentui/react/lib/Toggle";
import { CoObjectTypeEnum } from "../../../api/models";
import { ObjectPanelTabs } from "./ObjectPanelComponent";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { IconButton } from "@fluentui/react/lib/Button";
import { IContextualMenuItem } from "@fluentui/react";
import { CustomModal } from "../../layout/ActionModal";
import { lockObject } from "../../../redux/feature/cpm/cpm-slice";
import { isNotAuthorizedUser, isNotPresentFunction } from "../../../utils/user-function/userFunctionUtils";
import { UserFunctionEnum } from "../../../api/models/user-function-enum";
import { UserFunction } from "../../../api/models/user-function";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks/hooks";
import { selectUserFunctions } from "../../../redux/feature/settings/configurations-slice";
import { EDIT_OBJECT } from "../../../utils/constants/navigation";
import { navigate } from "../../../redux/feature/general/navigation-slice";
import { moveCursorToContentControlId } from "../../../office/WordDocumentPart";
import LoadingManager from "../../../common/loading/LoadingManager";

export const canBeAComponentObject = (coObjectItem: CoObjectItem) => {
  // if we have no info, we are during the creation of a new object
  if (!coObjectItem.info || !coObjectItem.id || coObjectItem.info.componentReference !== undefined) {
    return false;
  }

  const objectType = coObjectItem.info.objectType;
  return (
    objectType === CoObjectTypeEnum.TGK_TEXT ||
    objectType === CoObjectTypeEnum.AMOUNT ||
    objectType === CoObjectTypeEnum.TABLE ||
    objectType === CoObjectTypeEnum.DIMENSION_ATTRIBUTE ||
    objectType === CoObjectTypeEnum.CHART
  );
};

interface LockState {
  lock: boolean;
}

export interface ObjectTittleWithComponentFlagProps {
  titleProvider: () => string;
  coObjectItem: CoObjectItem;
  isDeleted: boolean;
  isEdit: boolean;
  isLocked: boolean;
  updateLockObjectInfo: (isLocked: boolean) => void;
  updateCoObjectInfo: (
    obj: CoObjectGeneralInfo,
    validationOutcome: ObjectPanelTabs,
    modified: boolean,
    modifiedType?: boolean,
    modifiedSource?: boolean,
  ) => void;
}

const RenderMenuIcon = () => <span />;

export const ObjectTitleWithComponentFlag: React.FunctionComponent<ObjectTittleWithComponentFlagProps> = (props) => {
  const [state, setState] = useState<LockState>({
    lock: false,
  });
  const { titleProvider, coObjectItem, isDeleted, isEdit, isLocked, updateLockObjectInfo } = props;
  const userFunctions: UserFunction[] = useAppSelector(selectUserFunctions);
  const onComponentToggle = (_: React.MouseEvent<HTMLElement, MouseEvent>, componentToggleChecked: boolean) => {
    const newInfo = { ...coObjectItem.info, isComponent: componentToggleChecked };
    props.updateCoObjectInfo(newInfo, undefined, true);
  };

  let notAuthorizedUserForLockObject = isNotAuthorizedUser(UserFunctionEnum.MENU_LOCK_OBJECT, userFunctions);
  let notPresentFunctionForLockObject = isNotPresentFunction(UserFunctionEnum.MENU_LOCK_OBJECT, userFunctions);

  const dispatch = useAppDispatch();
  if (state.lock) {
    return (
      <CustomModal
        modalTitle={formatMessage("cof.editobject.lockobject")}
        messageTitle={formatMessage("cof.editobject.lockobject.messagetitle")}
        message={formatMessage("cof.editobject.lockobject.message")}
        type={"confirm"}
        actions={[
          {
            title: formatMessage("cof.base.no").toUpperCase(),
            onClick: () => {
              setState((s) => ({ ...s, lock: false }));
            },
            variant: "white",
          },
          {
            title: formatMessage("cof.base.lock").toUpperCase(),
            onClick: async () => {
              LoadingManager.create("lock");
              await lockObject(coObjectItem, true);
              updateLockObjectInfo(true);
              await moveCursorToContentControlId(Number(coObjectItem.contentControlId));
              setTimeout(() => {
                dispatch(navigate(EDIT_OBJECT));
                LoadingManager.remove("lock");
              }, 1000);
            },
            variant: "blue",
          },
        ]}
        onHide={() => {}}
      ></CustomModal>
    );
  }

  const options: IContextualMenuItem[] = [
    {
      id: "lock_menuitem",
      key: "lock",
      text: formatMessage("cof.editobject.lockobject"),
      onClick: () => {
        setState((s) => ({ ...s, lock: true }));
      },
    },
  ];
  let titleClassName: string;
  if (coObjectItem.info?.isComponent && !isDeleted) {
    titleClassName = "component_title icon_class";
  } else if (coObjectItem.info?.componentReference && !isDeleted) {
    titleClassName = "instance_component_title icon_class";
  } else {
    titleClassName = "";
  }

  return (
    <>
      <div className={titleClassName} style={{ whiteSpace: "nowrap" }}>
        <h1
          style={{
            margin: 0,
            display: "inline",
            fontSize: "18px",
            fontWeight: 600,
            lineHeight: "20px",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            whiteSpace: "normal",
          }}
        >
          {titleProvider().trim()}
        </h1>
        {isEdit ? (
          <IconButton
            hidden={notPresentFunctionForLockObject}
            disabled={isLocked || notAuthorizedUserForLockObject}
            id={"lockObject"}
            iconProps={{ iconName: "MoreVertical" }}
            onRenderMenuIcon={RenderMenuIcon}
            menuProps={{ items: options }}
            style={{ color: "var(--color-blue)", height: "20px" }}
          />
        ) : null}
      </div>
      {canBeAComponentObject(coObjectItem) && !isDeleted && (
        <div>
          <Toggle
            id="component_toggle"
            styles={{
              label: { margin: "0 5px 0 0", padding: "0px", fontSize: "smaller", wordBreak: "keep-all" },
              root: { marginBottom: 0 },
            }}
            checked={coObjectItem.info.isComponent}
            label={formatMessage("cof.base.component")}
            inlineLabel
            onText=" "
            offText=" "
            onChange={(ev, checked) => onComponentToggle(ev, checked)}
            disabled={isLocked}
          />
        </div>
      )}
    </>
  );
};
